import { IconChevronDown } from "@tabler/icons-react";
import type { ReactNode } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

type SummaryCollapsibleRowProps = {
  // Accordion item identifier (also used to pre-open the row by default).
  value: string;
  label: ReactNode;
  // Optional content rendered before the label (e.g. addon thumbnails).
  leading?: ReactNode;
  // Aggregated value shown on the right of the collapsed header.
  amount?: ReactNode;
  amountClassName?: string;
  // The cart list scales the label and amount up on desktop (20px); the
  // configuration aside and checkout keep the 16px default.
  labelClassName?: string;
  // Class overrides so the row can drop its default borders / paddings when
  // embedded in an already-padded container (e.g. the checkout sidebar).
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  children: ReactNode;
};

// Collapsible summary row used by the addons and discount sections. The chevron
// sits next to the label (design layout) instead of the trailing position the
// default AccordionTrigger arrow would use, so the built-in arrow is disabled.
export const SummaryCollapsibleRow = ({
  value,
  label,
  leading,
  amount,
  amountClassName,
  labelClassName,
  className,
  triggerClassName,
  contentClassName,
  children,
}: SummaryCollapsibleRowProps) => (
  <AccordionItem className={cn("border-t border-b-0", className)} value={value}>
    <AccordionTrigger
      className={cn("group gap-3 p-4 hover:no-underline", triggerClassName)}
      showArrow={false}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {leading}
        <span
          className={cn(
            "flex min-w-0 items-center gap-1 font-medium text-base text-brand-green1-300",
            labelClassName,
          )}
        >
          <span className="truncate">{label}</span>
          <IconChevronDown
            className="size-6 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
            stroke={1.25}
          />
        </span>
      </span>
      {amount != null && (
        <span className={cn("shrink-0 font-bold text-base", amountClassName)}>
          {amount}
        </span>
      )}
    </AccordionTrigger>
    <AccordionContent className={cn("px-4 pt-0 pb-4", contentClassName)}>
      {children}
    </AccordionContent>
  </AccordionItem>
);
